import { CONTRACT_SCHEMA_VERSION } from "../domain/common";
import type {
  BoardContextImport,
  BoardContextImportPayload,
  CEOReviewExport,
  CEOReviewExportPayload,
  ContractEnvelope,
  DataImportReport,
  DataImportResult,
  JointCorrection,
  JointCorrectionPayload,
} from "../domain/types";
import { sha256 } from "./canonical-json";

type ContractProducer = ContractEnvelope<unknown>["producer"];

async function createEnvelope<T>(
  schemaName: ContractEnvelope<T>["schema_name"],
  producer: ContractProducer,
  payload: T,
  now = new Date().toISOString(),
): Promise<ContractEnvelope<T>> {
  return {
    schema_name: schemaName,
    schema_version: CONTRACT_SCHEMA_VERSION,
    contract_id: crypto.randomUUID(),
    produced_at: now,
    producer,
    payload_hash: await sha256(payload),
    payload,
  };
}

export function createCEOReviewExport(
  payload: CEOReviewExportPayload,
  now?: string,
): Promise<CEOReviewExport> {
  return createEnvelope("ceo-review-export", "ceo-os", payload, now);
}

export function createBoardContextImport(
  payload: BoardContextImportPayload,
  now?: string,
): Promise<BoardContextImport> {
  return createEnvelope("board-context-import", "digital-board", payload, now);
}

export function createJointCorrection(
  payload: JointCorrectionPayload,
  now?: string,
): Promise<JointCorrection> {
  return createEnvelope("joint-correction", "ceo-os-joint-adapter", payload, now);
}

export function createDataImportReport(
  result: DataImportResult,
  sourceSchemaVersion: string,
  now?: string,
): Promise<DataImportReport> {
  return createEnvelope(
    "data-import-report",
    "ceo-os",
    {
      import_id: result.id,
      imported_at: result.importedAt,
      source_file_name: result.sourceFileName,
      source_schema_name: result.schemaName,
      source_schema_version: sourceSchemaVersion,
      status: result.status,
      added: result.added,
      skipped: result.skipped,
      conflicts: result.conflicts.map((conflict) => ({
        entity_type: conflict.entityType,
        incoming_id: conflict.incomingId,
        ...(conflict.localId === undefined ? {} : { local_id: conflict.localId }),
        reason: conflict.reason,
      })),
      errors: result.errors,
    },
    now,
  );
}
